import { useContext, useEffect, useRef } from 'react';
import useAppDispatch from 'hooks/useAppDispatch';
import {
  propertyCalendarConditionGraphQLQuery,
  propertyCalendarGraphQLQuery,
} from 'pages/virtualizedStackedCalendar/StackedCalendar.utils';
import {
  setIsLoadingProperties,
  setNextPropertiesOffset,
  updateVisibleProperties,
} from 'store/slices/stackedCalendar';
import { getUnitType } from 'utils/lead/leadUtils';
import getPropertiesQuery from 'utils/property/getPropertiesQuery';
import { usePropertiesCount } from 'components/domain/propertiesList/usePropertiesCount';
import useBillablePropertiesCount from 'components/domain/propertiesList/useBillablePropertiesCount';
import useFeatures from 'hooks/useFeatures';
import useAppQuery from '../../../hooks/useAppQuery';
import API from '../../../services/API';
import useAppUser from '../../../hooks/useAppUser';
import CalendarContext from '../CalendarContext';
import {
  getCalendarFilterLocalStorageKey,
  setPropertiesBusinessType,
  stackedCalendarStyleConstants,
  stackedCalendarPropertiesConstants,
} from '../Calendar.constants';
import useFetchAllProperties from '../../../components/domain/property/useFetchAllProperties';
import { GetCalendarPropertiesResponse } from '../Calendar.types';

const otherUiItemsHeight = 85 + 83 + 100 + 30; // main nav + calendar page title + months container + margin bottom
function getInitialPropertiesLimit() {
  const availablePropertiesHeight = window.innerHeight - otherUiItemsHeight;
  const limit =
    Math.ceil(
      availablePropertiesHeight / stackedCalendarStyleConstants.days.height,
    ) + 2;
  return Math.max(limit, 25); // load at least 25 properties
}

const useCalendarProperties = () => {
  const previousPropertiesLength = useRef<number>(0);
  const previousPropertiesLimit = useRef<number>(
    stackedCalendarPropertiesConstants.defaultLimit,
  );
  const { agencyUid, isOwner, userUid } = useAppUser();
  const storedFilter = localStorage.getItem(
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    getCalendarFilterLocalStorageKey(userUid),
  );
  const {
    filter,
    setTotalPropertiesNumber,
    updatePropertiesMap,
    setAllProperties,
    selectedProperty,
  } = useContext(CalendarContext);
  const dispatch = useAppDispatch();
  // Flag OFF = master behavior; ON = the property-loading optimizations.
  const { allowCalendarPropertiesOptimization: isPropertiesOptimized } =
    useFeatures();
  const { calculateAmountProperties } = useBillablePropertiesCount();

  const { offset } = filter;
  const isNewSearch = offset === 0;

  const { data: propertiesCount } = usePropertiesCount();

  // Optimized: fetch the full tree only for the deep-link view. Master: eager on each new search.
  const {
    allProperties: fetchedAllProperties,
    isFetching: isAllPropertiesLoading,
  } = useFetchAllProperties({
    enabled: isPropertiesOptimized ? !!selectedProperty : isNewSearch,
    conditions: propertyCalendarConditionGraphQLQuery,
    fields: propertyCalendarGraphQLQuery,
  });

  const { data: fetchedPropertiesData, isLoading: isPropertiesLoading } =
    useAppQuery(
      ['properties-calendar', agencyUid, filter, selectedProperty],
      async () => {
        const limit = isNewSearch ? getInitialPropertiesLimit() : filter.limit;
        previousPropertiesLimit.current =
          limit ?? stackedCalendarPropertiesConstants.defaultLimit;

        const propertyFilter = {
          ...filter,
          limit,
        };

        let queryStr = getPropertiesQuery({
          filters: {
            ...propertyFilter,
            // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            agencyUid: !isOwner && agencyUid,
          },
        });

        if (selectedProperty) {
          const propertyUidToFetch =
            // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            getUnitType(fetchedAllProperties, selectedProperty)?.uid ||
            selectedProperty;
          queryStr += `,propertyUids:"${propertyUidToFetch}"`;
          // always fetch the selectedProperty with isActive: true || false:
          queryStr = queryStr.replace(/isActive: (true|false),?/, '');
        }

        const response = await API.post<GetCalendarPropertiesResponse>(
          '/v3/graphql',
          {
            query: `{properties (${queryStr}){
              ${propertyCalendarGraphQLQuery}
            }}`,
            variables: {},
          },
        );

        previousPropertiesLength.current = response.data.data.properties.length;

        return { limit, properties: response.data.data.properties };
      },
      {
        // Optimized: paint the first page without waiting on the full fetch (unless deep-linked).
        enabled:
          !!agencyUid &&
          !!filter &&
          (isPropertiesOptimized
            ? !selectedProperty || !!fetchedAllProperties || !!storedFilter
            : !!fetchedAllProperties || !!storedFilter),
      },
    );

  useEffect(() => {
    if (fetchedAllProperties) {
      setAllProperties(fetchedAllProperties);

      // Master derives the count from the full fetch; optimized uses the count endpoint.
      if (!isPropertiesOptimized) {
        setTotalPropertiesNumber(
          calculateAmountProperties(fetchedAllProperties),
        );
      }
    }
  }, [
    fetchedAllProperties,
    isPropertiesOptimized,
    setAllProperties,
    calculateAmountProperties,
    setTotalPropertiesNumber,
  ]);

  useEffect(() => {
    if (isPropertiesOptimized && propertiesCount != null) {
      setTotalPropertiesNumber(propertiesCount);
    }
  }, [isPropertiesOptimized, propertiesCount, setTotalPropertiesNumber]);

  useEffect(() => {
    // Optimized ignores the background full fetch so a slow one can't block the list; master includes it.
    dispatch(
      setIsLoadingProperties(
        isPropertiesOptimized
          ? isPropertiesLoading && isNewSearch
          : isAllPropertiesLoading || (isPropertiesLoading && isNewSearch),
      ),
    );
  }, [
    isPropertiesOptimized,
    isAllPropertiesLoading,
    isPropertiesLoading,
    isNewSearch,
    dispatch,
  ]);

  useEffect(() => {
    // offset/isNewSearch/selectedProperty are part of the query key, so data resets to undefined when they
    // change — never re-run these appends with a stale batch by adding keepPreviousData to the query.
    if (fetchedPropertiesData) {
      const { limit, properties } = fetchedPropertiesData;
      // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      dispatch(setNextPropertiesOffset(limit + offset));
      updatePropertiesMap({ properties });
      setPropertiesBusinessType(properties);
      dispatch(
        updateVisibleProperties({ properties, appendToExisting: !isNewSearch }),
      );

      // Optimized list view: allProperties mirrors the loaded batch (master/deep-link use the full fetch above).
      if (isPropertiesOptimized && !selectedProperty) {
        setAllProperties((previous) =>
          isNewSearch ? properties : [...previous, ...properties],
        );
      }
    }
  }, [
    fetchedPropertiesData,
    offset,
    updatePropertiesMap,
    isNewSearch,
    dispatch,
    setAllProperties,
    selectedProperty,
    isPropertiesOptimized,
  ]);

  return {
    previousPropertiesLength: previousPropertiesLength.current,
    previousPropertiesLimit: previousPropertiesLimit.current,
  };
};

export default useCalendarProperties;
